/**
 * Vocabulário de tela do incidente — o que cada tipo SIGNIFICA para quem opera,
 * e qual ação resolve.
 *
 * Mesmo espírito de `features/monitor/monitor-ui.ts`: fica separado da tela
 * porque o teste consegue afirmar sobre as frases sem montar a página inteira, e
 * porque o mesmo texto serve a lista, ao contador do menu e ao aviso do topo sem
 * ninguém reescrever "Número fora do ar" de cabeça.
 *
 * Nada aqui decide regra de negócio: quem abre, reconhece e resolve incidente é o
 * servidor. Isto é tradução, não cálculo — e é o oposto de imprimir o enum na
 * tela. `MONITOR_OFFLINE` é o nome que a TABELA dá ao problema; quem está de pé
 * no celular às 23h precisa ler "o número parou e nada está sendo registrado".
 *
 * De propósito este arquivo NÃO importa tipo de `lib/api`: ele descreve o
 * vocabulário da tela. Quem casa vocabulário com o que o servidor manda é a
 * página, no `INCIDENT_STATUS[incident.status]` — se o servidor mudar o enum, o
 * erro aparece lá, no typecheck, e não num rótulo em branco em produção.
 */
import {
  AlertOctagon,
  ArrowLeftRight,
  Unlink,
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  CircleDot,
  Eye,
  Info,
  Lock,
  Radio,
  Shuffle,
  TrendingDown,
  Users,
  UserX,
} from 'lucide-react';
import type { ComponentType } from 'react';
import type { BadgeTone, IncidentSeverity } from '@mgdlead/ui';

export type { IncidentSeverity };

/* ========================================================================== */
/*  Situação                                                                   */
/* ========================================================================== */

export type IncidentStatus = 'OPEN' | 'ACKNOWLEDGED' | 'RESOLVED';

/** O filtro da tela: as três situações + "tudo". Abertos é o padrão. */
export type IncidentFilter = IncidentStatus | 'ALL';

export interface IncidentStatusSpec {
  label: string;
  tone: BadgeTone;
  icon: ComponentType<{ className?: string }>;
  /** Uma frase por situação, em português de gente. */
  hint: string;
}

/**
 * Reconhecido e resolvido são coisas DIFERENTES, e a tela precisa mostrar isso —
 * senão "reconhecer" vira um jeito de fazer o incidente sumir sem ninguém ter
 * consertado nada, que é exatamente o contrário do que o botão serve.
 *
 * A anatomia é a mesma nos três (ícone + rótulo + cor): o estado se reconhece
 * pela FORMA, não só pela cor (DESIGN_SYSTEM §1.3 — há daltônico do outro lado).
 */
export const INCIDENT_STATUS: Record<IncidentStatus, IncidentStatusSpec> = {
  OPEN: {
    label: 'Aberto',
    tone: 'warning',
    icon: CircleDot,
    hint: 'Ninguém assumiu ainda. Continua acontecendo enquanto estiver assim.',
  },
  ACKNOWLEDGED: {
    label: 'Reconhecido',
    tone: 'info',
    icon: Eye,
    hint: 'Alguém viu e está cuidando. O problema continua de pé: reconhecer não conserta, só avisa o resto do time que já tem dono.',
  },
  RESOLVED: {
    label: 'Resolvido',
    tone: 'success',
    icon: CheckCircle2,
    hint: 'Acabou. Se o mesmo problema voltar a acontecer, entra um incidente NOVO na lista.',
  },
};

/** As abas do filtro, na ordem em que a operação olha. */
export const INCIDENT_FILTERS: readonly { value: IncidentFilter; label: string }[] = [
  { value: 'OPEN', label: 'Abertos' },
  { value: 'ACKNOWLEDGED', label: 'Reconhecidos' },
  { value: 'RESOLVED', label: 'Resolvidos' },
  { value: 'ALL', label: 'Todos' },
];

/**
 * O filtro que a tela abre.
 *
 * "Abertos" e não "Todos": quem entra aqui quer saber o que está quebrado AGORA,
 * e uma lista que começa com meses de incidente resolvido enterra os três de hoje.
 */
export const DEFAULT_INCIDENT_FILTER: IncidentFilter = 'OPEN';

/* ========================================================================== */
/*  Severidade                                                                 */
/* ========================================================================== */

export interface IncidentSeveritySpec {
  label: string;
  tone: BadgeTone;
  icon: ComponentType<{ className?: string }>;
  hint: string;
}

/** Os rótulos são os do DESIGN_SYSTEM §8 — "Info" / "Atenção" / "Crítico". */
export const INCIDENT_SEVERITY: Record<IncidentSeverity, IncidentSeveritySpec> = {
  INFO: {
    label: 'Info',
    tone: 'info',
    icon: Info,
    hint: 'Registro de acontecimento. Não exige nada de ninguém agora.',
  },
  WARNING: {
    label: 'Atenção',
    tone: 'warning',
    icon: AlertTriangle,
    hint: 'Alguma coisa está fora do lugar e vai piorar se ficar assim.',
  },
  CRITICAL: {
    label: 'Crítico',
    tone: 'danger',
    icon: AlertOctagon,
    hint: 'Está custando operação enquanto você lê. É o que não espera pela próxima olhada.',
  },
};

/* ========================================================================== */
/*  Tipos de incidente                                                         */
/* ========================================================================== */

export type IncidentType =
  | 'DATA_DIVERGENCE'
  | 'ROLLUP_DIVERGENCE'
  | 'NO_GROUP_AVAILABLE'
  | 'MONITOR_OFFLINE'
  | 'EVASION_HIGH'
  | 'GROUP_EXIT_BURST'
  | 'GROUP_INVITE_MISSING'
  | 'GROUP_INVITE_REVOKED'
  | 'GROUP_INVITE_WRONG_GROUP'
  | 'GROUP_GROWTH_LOCKED'
  | 'GROUP_NO_JOINS';

/**
 * D1: os incidentes de "convite quebrado" — os que se resolvem COLANDO o link novo do grupo
 * no próprio card. O servidor confere o link no WhatsApp antes de gravar.
 */
export const BROKEN_INVITE_TYPES: readonly IncidentType[] = [
  'GROUP_INVITE_MISSING',
  'GROUP_INVITE_REVOKED',
  'GROUP_INVITE_WRONG_GROUP',
];

export function isBrokenInviteIncident(type: string): boolean {
  return (BROKEN_INVITE_TYPES as readonly string[]).includes(type);
}

export interface IncidentTypeSpec {
  /** O nome do problema em português de gente. Nunca o enum. */
  label: string;
  /** O que aconteceu, e por que isso importa para a operação. */
  happened: string;
  /** A ação que resolve — dita como gesto, não como conceito. */
  action: string;
  icon: ComponentType<{ className?: string }>;
}

/**
 * As quatro traduções.
 *
 * Cada uma responde as três perguntas de quem abre esta tela às 23h: **o que
 * quebrou, desde quando, o que fazer**. "Desde quando" é o carimbo que a linha já
 * carrega; as outras duas estão aqui.
 */
export const INCIDENT_TYPE: Record<IncidentType, IncidentTypeSpec> = {
  DATA_DIVERGENCE: {
    label: 'Contagem que não bate',
    happened:
      'A contagem de pessoas que o WhatsApp informa para este grupo não bate com a soma de entradas e saídas que registramos. Alguma movimentação passou sem ser vista, e é ela que falta no seu número.',
    action:
      'Abra o grupo e toque em Sincronizar pessoas: a varredura completa refaz a lista de quem está lá dentro e devolve a contagem ao lugar.',
    icon: Users,
  },
  ROLLUP_DIVERGENCE: {
    label: 'Número do painel fora do lugar',
    happened:
      'O resumo do dia guardado para o painel não bateu com a reconferência feita a partir do histórico. Enquanto isso, um número do dashboard pode estar diferente do que o histórico mostra.',
    action:
      'Não há nada a apertar: o histórico é a fonte e o resumo é reconstruído na próxima conferência. O que vale é olhar se o mesmo dia continua divergindo — aí é caso de falar com o suporte antes de decidir alguma coisa por esse número.',
    icon: BarChart3,
  },
  NO_GROUP_AVAILABLE: {
    label: 'Link sem grupo com vaga',
    happened:
      'Chegou clique no redirecionador e nenhum grupo da campanha tinha vaga dentro do limite de segurança. Quem clicou não entrou em grupo nenhum: no melhor caso caiu no destino de reserva do link — e você pagou pelo clique do mesmo jeito.',
    action:
      'Dê vaga à campanha: ligue mais um grupo, retome um que está pausado ou reveja o limite de segurança dos que já estão lá.',
    icon: Shuffle,
  },
  MONITOR_OFFLINE: {
    label: 'Número fora do ar',
    happened:
      'O número que monitora os grupos parou de mandar movimentação. Entrada e saída de gente não estão sendo registradas enquanto ele estiver assim — e silêncio não aparece no painel como queda, aparece como zero.',
    action:
      'Abra os monitoradores e reconecte o número. Se o painel pedir o QR, é preciso ler o código com o celular em mãos.',
    icon: Radio,
  },
  EVASION_HIGH: {
    label: 'Tráfego que não fica',
    happened:
      'Uma campanha ou um anúncio está trazendo gente que sai por conta própria já no primeiro dia — bem acima do normal do seu workspace. A verba está comprando entradas que não ficam.',
    action:
      'Abra a campanha, aba Retenção, e veja qual anúncio puxa a evasão. Sugestão: revisar o criativo ou reduzir o orçamento dele. Nada é pausado sozinho.',
    icon: TrendingDown,
  },
  GROUP_EXIT_BURST: {
    label: 'Rajada de saídas no grupo',
    happened:
      'Muita gente saiu por conta própria deste grupo em poucas horas — bem acima do normal dele. Costuma ser uma mensagem ou um disparo que espantou as pessoas.',
    action:
      'Confira as últimas mensagens do grupo antes de mandar a próxima. Quem o admin removeu não entra nesta conta.',
    icon: TrendingDown,
  },
  GROUP_INVITE_MISSING: {
    label: 'Grupo sem convite',
    happened:
      'Este grupo está numa campanha, tem vaga, e mesmo assim não recebe ninguém: não temos o link de convite dele (ou o link foi trocado no celular). Quem clica é mandado para outro grupo ou para o destino de reserva.',
    action:
      'Cole aqui o link novo do grupo (no WhatsApp: dados do grupo › Convidar via link). Nós conferimos o link antes de usar.',
    icon: Unlink,
  },
  GROUP_INVITE_REVOKED: {
    label: 'Convite trocado no celular',
    happened:
      'O link que estava publicado deixou de abrir o grupo — alguém redefiniu o convite pelo celular, e o WhatsApp não avisa. Quem clicou nesse meio-tempo viu “link revogado”.',
    action:
      'Se o link novo ainda não foi lido sozinho, cole-o aqui. Depois, confira se é preciso trocar o link em algum anúncio.',
    icon: Unlink,
  },
  GROUP_INVITE_WRONG_GROUP: {
    label: 'Link de outro grupo',
    happened:
      'O link publicado para este grupo abre OUTRO grupo. Quem clicou entrou no grupo errado — e isso não aparece em nenhum outro número do painel.',
    action: 'Cole aqui o link certo deste grupo. Nós conferimos para onde ele leva antes de usar.',
    icon: ArrowLeftRight,
  },
  GROUP_GROWTH_LOCKED: {
    label: 'WhatsApp travou o grupo',
    happened:
      'O link do grupo abre, mas o WhatsApp não deixa ninguém novo entrar — costuma acontecer quando o grupo cresce rápido demais ou recebe denúncias. Já tiramos o grupo da distribuição; quando o WhatsApp liberar, ele volta sozinho.',
    action:
      'Confira se a campanha ainda tem grupos com vaga. Se não tiver, ative um grupo novo para não perder os próximos cliques.',
    icon: Lock,
  },
  GROUP_NO_JOINS: {
    label: 'Cliques sem entrada',
    happened:
      'Este grupo continua recebendo cliques e ninguém entra, com o número conectado. O link pode ter sido trocado, ou o WhatsApp pode estar bloqueando entradas.',
    action:
      'Abra o link do grupo num celular que não está nele. Se aparecer “link revogado”, cole o link novo; se o grupo não aceitar novos membros, tire-o da campanha e ative outro.',
    icon: UserX,
  },
};

/**
 * `null` quando o servidor mandou um tipo que esta versão do painel não conhece.
 *
 * Acontece de verdade: o servidor sobe antes do front num deploy. A tela cai no
 * `title`/`description` que vieram prontos em vez de imprimir `FOO_BAR` na cara
 * de quem está resolvendo o problema.
 */
export function incidentTypeSpec(type: string): IncidentTypeSpec | null {
  return Object.prototype.hasOwnProperty.call(INCIDENT_TYPE, type)
    ? INCIDENT_TYPE[type as IncidentType]
    : null;
}

/** O que a tela diz quando não conhece o tipo. Honesto, e sem o enum. */
export const UNKNOWN_TYPE_ACTION =
  'Este tipo de incidente é mais novo que esta versão do painel. Recarregue a página; se continuar assim, fale com o suporte.';

/* ========================================================================== */
/*  Ações                                                                      */
/* ========================================================================== */

export type IncidentAction = 'acknowledge' | 'resolve';

export const INCIDENT_ACTION: Record<IncidentAction, string> = {
  acknowledge: 'Reconhecer',
  resolve: 'Resolver',
};

/**
 * A diferença entre os dois botões, escrita na tela.
 *
 * Sem isso, "Reconhecer" e "Resolver" são dois botões cinzas com verbos parecidos
 * — e o que acontece é alguém reconhecer tudo achando que limpou a lista.
 */
export const INCIDENT_ACTION_HINT: Record<IncidentAction, string> = {
  acknowledge: 'Eu vi e estou cuidando. O incidente continua aberto para o time.',
  resolve: 'Acabou. Se acontecer de novo, entra um incidente novo.',
};

export const INCIDENT_ACTION_EXPLAINER =
  'Reconhecer é dizer “eu vi e estou cuidando” — o problema continua de pé, só ganha dono. Resolver é dizer que acabou; se voltar a acontecer, um incidente novo é aberto.';

/**
 * Qual ação tem DESTAQUE em cada situação — uma só por linha.
 *
 * Hierarquia é conteúdo: esta mesma tela já nasceu errada em outro lugar do
 * projeto, com cinco botões `ghost` idênticos, e o dono do produto não achou a
 * ação. Aqui só existe um botão com contorno por incidente, e ele muda com o
 * estado: aberto pede dono, reconhecido pede desfecho, resolvido não pede nada.
 */
export function primaryActionFor(status: IncidentStatus): IncidentAction | null {
  switch (status) {
    case 'OPEN':
      return 'acknowledge';
    case 'ACKNOWLEDGED':
      return 'resolve';
    case 'RESOLVED':
      return null;
  }
}

/* ========================================================================== */
/*  Caminho para o lugar do problema                                           */
/* ========================================================================== */

export interface IncidentEntityLink {
  to: string;
  label: string;
}

/**
 * Para onde vai quem quer CONSERTAR o incidente.
 *
 * Incidente sem caminho obriga a pessoa a caçar o grupo certo no menu — e este
 * projeto já teve 15 telas sem caminho no celular. O link sai da entidade que o
 * servidor apontou.
 *
 * Duas honestidades aqui:
 *  - só o grupo tem tela própria por id (`grupos/:groupId`); monitorador,
 *    redirecionador e campanha levam para a LISTA, e o rótulo diz isso, em vez de
 *    prometer um detalhe que não existe;
 *  - `entityType` desconhecido não vira link chutado. Sem caminho é melhor que
 *    caminho errado.
 *
 * A normalização (minúsculas, último segmento, plural fora) existe porque o nome
 * vem do servidor como texto livre e pode chegar `GROUP`, `group`,
 * `whatsapp_group` ou `groups` — e nenhum desses deveria mudar o que a pessoa vê.
 */
const ENTITY_DESTINATION: Record<string, (slug: string, id: string | null) => IncidentEntityLink> =
  {
    group: (slug, id) =>
      id === null
        ? { to: `/w/${slug}/grupos`, label: 'Ver os grupos' }
        : { to: `/w/${slug}/grupos/${id}`, label: 'Abrir o grupo' },
    monitor: (slug) => ({ to: `/w/${slug}/monitoradores`, label: 'Abrir os monitoradores' }),
    redirector: (slug) => ({
      to: `/w/${slug}/redirecionadores`,
      label: 'Abrir os redirecionadores',
    }),
    campaign: (slug) => ({ to: `/w/${slug}/campanhas`, label: 'Abrir as campanhas' }),
  };

export function incidentEntityLink(
  entity: { entityType: string | null; entityId: string | null },
  slug: string,
): IncidentEntityLink | null {
  if (entity.entityType === null) return null;

  const normalizado = entity.entityType.toLowerCase().replace(/[^a-z]/g, '');
  const semPlural = normalizado.endsWith('s') ? normalizado.slice(0, -1) : normalizado;

  // Casa pelo FINAL: `whatsapp_group`, `WhatsappGroups` e `GROUP` são todos o mesmo lugar.
  const chave = Object.keys(ENTITY_DESTINATION).find((nome) => semPlural.endsWith(nome));
  const destination = chave === undefined ? undefined : ENTITY_DESTINATION[chave];
  return destination === undefined ? null : destination(slug, entity.entityId);
}

/* ========================================================================== */
/*  Vazio                                                                      */
/* ========================================================================== */

export interface IncidentEmptySpec {
  /**
   * `zero` quando a lista CHEGOU e veio vazia — é um número conhecido e é boa
   * notícia. `no-data` seria "não sabemos", que é outra coisa (invariante 8).
   */
  kind: 'zero';
  title: string;
  description: string;
}

/**
 * "Nenhum incidente aberto" não é uma tela triste de erro: é o estado em que o
 * produto deveria viver. Por isso texto próprio por filtro, e nunca o ErrorState.
 */
export const INCIDENT_EMPTY: Record<IncidentFilter, IncidentEmptySpec> = {
  OPEN: {
    kind: 'zero',
    title: 'Nenhum incidente aberto',
    description:
      'Os números estão no ar, os grupos têm vaga e as contagens batem. Nada para você fazer agora — este é o estado normal, não uma tela sem dados.',
  },
  ACKNOWLEDGED: {
    kind: 'zero',
    title: 'Nenhum incidente sendo cuidado',
    description:
      'Ninguém marcou um incidente como “estou cuidando” neste momento. Veja os abertos para assumir algum.',
  },
  RESOLVED: {
    kind: 'zero',
    title: 'Nenhum incidente resolvido ainda',
    description:
      'Quando um incidente for resolvido, ele fica guardado aqui com a data e quem cuidou.',
  },
  ALL: {
    kind: 'zero',
    title: 'Nenhum incidente até agora',
    description:
      'Desde que o monitoramento começou, nada precisou da sua atenção. Os incidentes aparecem sozinhos aqui quando o sistema detecta um problema.',
  },
};

/* ========================================================================== */
/*  Contador do menu                                                           */
/* ========================================================================== */

export interface IncidentBadge {
  count: number;
  tone: 'danger' | 'warning';
}

/**
 * O contador ao lado de "Incidentes" no menu.
 *
 * `undefined` em dois casos, e os dois importam:
 *  - a contagem ainda NÃO CHEGOU — mostrar `0` aqui seria o painel afirmando
 *    "está tudo bem" sem ter perguntado (invariante 8);
 *  - a contagem chegou e é zero — não há nada a contar, e um `0` grudado no menu
 *    o dia inteiro é ruído que ninguém mais enxerga.
 *
 * Sólido/vermelho só com crítico no meio: é a regra do DESIGN_SYSTEM §8 (badge
 * numérico na sidebar é privilégio do CRITICAL).
 */
export function incidentBadge(
  counts: { open: number; critical: number } | undefined,
): IncidentBadge | undefined {
  if (counts === undefined) return undefined;
  if (counts.open <= 0) return undefined;
  return { count: counts.open, tone: counts.critical > 0 ? 'danger' : 'warning' };
}
